// frontend/src/pages/Reviews.js
// Redesigned: review form pre-populates KPIs for the selected employee+cycle.
// Workflow shown: self-eval step → manager scoring step → final score.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getReviews, getCycles, getUsers, deleteReview, createCycle,
  getKPIAssignments, getPrograms,
  getCycleEligibility, markNotEligible, markEligible
} from '../services/api';
import {
  Card, UserLink, Badge, Btn, Modal, Field, Input, Select, Textarea,
  SectionTitle, Spinner, RatingPill,
} from '../components/UI';
import PageHeader from '../components/PageHeader';

const scoreColor = s => {
  if (!s) return 'var(--text-3)';
  if (s >= 4.5) return '#7A5E00';
  if (s >= 3.8) return 'var(--navy-600)';
  if (s >= 3.0) return 'var(--blue-700)';
  if (s >= 2.0) return 'var(--amber-700)';
  return 'var(--red-700)';
};

export default function Reviews() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [reviews,  setReviews]  = useState([]);
  const [cycles,   setCycles]   = useState([]);
  const [users,    setUsers]    = useState([]);
  const [assignments, setAssignments] = useState([]); // KPI assignments for the current cycle
  const [loading,  setLoading]  = useState(true);
  const [cycleId,  setCycleId]  = useState('');
  const [showCycle,setShowCycle]= useState(false);
  const [saving,   setSaving]   = useState(false);

  const [cycleForm, setCycleForm] = useState({ name: '', start_date: '', end_date: '', status: 'active', cycle_number: '', year: new Date().getFullYear(), program_id: '1' });
  const [programs, setPrograms] = useState([]);

  const [filterReviewer, setFilterReviewer] = useState('');
  // Who is marked NOT eligible for the selected cycle, keyed by employee id.
  const [exclusions, setExclusions] = useState({});
  const [exclModal, setExclModal]   = useState(null);   // { id, name } being excluded
  const [exclReason, setExclReason] = useState('');
  const isAdmin   = user?.role === 'admin';
  const isManager = ['admin','manager','dept_head'].includes(user?.role);

  const load = useCallback(async () => {
    const [r, c, u, p] = await Promise.all([
      getReviews().catch(() => []),
      getCycles().catch(() => []),
      getUsers().catch(() => []),
      getPrograms().catch(() => []),
    ]);
    const reviews = Array.isArray(r) ? r : [];
    const cycles  = Array.isArray(c) ? c : [];
    const users   = Array.isArray(u) ? u : [];
    const sortedCycles = cycles.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
    setReviews(reviews); setCycles(sortedCycles); setUsers(users);
    setPrograms(Array.isArray(p) ? p : []);
    const activeCycle = sortedCycles.find(x => x.status === 'active');
    if (activeCycle && !cycleId) setCycleId(String(activeCycle.id));
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  // Refetch KPI assignments whenever the selected cycle changes so the
  // self-evaluation status on each card stays accurate.
  useEffect(() => {
    if (!cycleId) { setAssignments([]); return; }
    getKPIAssignments({ cycle_id: cycleId })
      .then(d => setAssignments(Array.isArray(d) ? d : []))
      .catch(() => setAssignments([]));
  }, [cycleId]);

  // Exclusions for the selected cycle, keyed by employee id for O(1) card lookup.
  const loadExclusions = useCallback(() => {
    if (!cycleId) { setExclusions({}); return Promise.resolve(); }
    return getCycleEligibility(cycleId)
      .then(list => {
        const byEmp = {};
        (Array.isArray(list) ? list : []).forEach(x => { byEmp[String(x.employee_id)] = x; });
        setExclusions(byEmp);
      })
      .catch(() => setExclusions({}));
  }, [cycleId]);

  useEffect(() => { loadExclusions(); }, [loadExclusions]);

  const submitExclusion = async (e) => {
    e.preventDefault();
    if (!exclReason.trim()) return;
    setSaving(true);
    try {
      await markNotEligible(Number(cycleId), exclModal.id, exclReason.trim());
      await loadExclusions();
      setExclModal(null); setExclReason('');
    } catch (err) {
      alert(err.response?.data?.error || 'Could not mark not eligible');
    } finally { setSaving(false); }
  };

  const restoreEligibility = async (employee) => {
    const excl = exclusions[String(employee.id)];
    if (!excl) return;
    if (!window.confirm(`Mark ${employee.name} eligible for this cycle again?`)) return;
    try {
      await markEligible(excl.id);
      await loadExclusions();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not mark eligible');
    }
  };

  // Derive the employee's review state for the selected cycle:
  //   'pending_manager' — every KPI assignment has a self_score → manager's turn
  //   'in_progress'     — some KPI assignments self-scored, not all
  //   'not_started'     — no self-scores yet (or no KPIs assigned)
  const selfEvalStatus = (employeeId) => {
    const mine = assignments.filter(a => String(a.employee_id) === String(employeeId));
    if (mine.length === 0) return 'not_started';
    const scored = mine.filter(a => a.self_score != null);
    if (scored.length === 0) return 'not_started';
    if (scored.length === mine.length) return 'pending_manager';
    return 'in_progress';
  };

  const openReviewForm = (reviewee) => {
    if (!cycleId) { alert('Pick a cycle first'); return; }
    navigate(`/reviews/edit/${reviewee.id}?cycle=${cycleId}`);
  };

  const handleDeleteReview = async (id, name) => {
    if (!window.confirm(`Archive review for ${name}? It will be hidden from the system.`)) return;
    try { await deleteReview(id); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Error archiving review'); }
  };

  const handleCreateCycle = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await createCycle({ ...cycleForm, program_id: cycleForm.program_id ? Number(cycleForm.program_id) : 1 });
      await load(); setShowCycle(false);
    } catch (err) { alert(err.response?.data?.error || 'Error creating cycle'); }
    finally { setSaving(false); }
  };

  const managers = users.filter(u => ['admin','manager'].includes(u.role));
  const filtered = reviews
    .filter(r => !cycleId || String(r.cycle_id) === cycleId)
    .filter(r => !filterReviewer || String(r.reviewer_id) === filterReviewer);
  const currentCycle = cycles.find(c => String(c.id) === cycleId);
  const cycleClosed  = currentCycle?.status === 'closed' && Number(currentCycle?.program_id) === 1;

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="reviews" helpTask="manager-score" subtitle={'KPI-based review cycles · 3 per year'} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAdmin && (
            <select value={filterReviewer} onChange={e => setFilterReviewer(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 12, border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-1)' }}>
              <option value="">All reviewers</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          )}
          {isAdmin && (
            <Btn variant="secondary" size="sm" onClick={() => setShowCycle(true)}>+ New cycle</Btn>
          )}
        </div>
      </div>

      {/* Cycle selector */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24 }}>
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>Cycle:</span>
        <select value={cycleId} onChange={e => setCycleId(e.target.value)}
          style={{ padding: '7px 12px', fontSize: 13, border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-1)', minWidth: 220 }}>
          <option value="">All cycles</option>
          {cycles.map(c => (
            <option key={c.id} value={String(c.id)}>
              {c.name}{c.is_annual ? ' (Annual)' : ''} · {c.start_date} to {c.end_date} — {c.status === 'active' ? 'Live' : c.status}
            </option>
          ))}
        </select>
      </div>

      {/* Cycle info banner */}
      {currentCycle && (
        <div style={{
          background: currentCycle.is_annual ? 'var(--amber-50)' : 'var(--navy-50)',
          border: `0.5px solid ${currentCycle.is_annual ? '#FAC775' : 'var(--navy-100)'}`,
          borderRadius: 8, padding: '10px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ fontSize: 13, color: currentCycle.is_annual ? 'var(--amber-700)' : 'var(--navy-600)' }}>
            {currentCycle.is_annual
              ? '🏆 Annual cycle — KPI scores from this cycle feed into end-of-year bonus calculations'
              : `📋 Cycle ${currentCycle.cycle_number || ''} of 3 · ${currentCycle.name}`}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
            {currentCycle.start_date} → {currentCycle.end_date}
          </div>
        </div>
      )}

      {cycleClosed && (
        <div style={{
          background: 'var(--gray-50)', borderLeft: '3px solid var(--text-3)',
          borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--text-2)',
        }}>
          This cycle is closed — reviews and KPI ratings are read-only.
        </div>
      )}

      {/* Team review cards (manager view) */}
      {isManager && !cycleClosed && (
        <div style={{ marginBottom: 28 }}>
          <SectionTitle>Start or continue a review</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10, marginTop: 10 }}>
            {users.filter(u => u.id !== user?.id && u.role !== 'admin')
              .filter(u => !filterReviewer || String(u.manager_id) === filterReviewer)
              .map(u => {
              const existingReview = filtered.find(r => String(r.reviewee_id) === String(u.id) && r.review_type === 'manager');
              const excl = exclusions[String(u.id)];
              // An excluded person is out of the cycle: no review to start, so the
              // card stops being a link and states why instead.
              if (excl) {
                return (
                  <Card key={u.id} style={{ padding: '12px 14px', opacity: 0.72, background: 'var(--gray-50)' }}>
                    <UserLink id={u.id} name={u.name} initials={u.avatar_initials} subtitle={u.job_title}
                      avatarSize={32} nameSize={12} gap={9} style={{ marginBottom: 8 }} />
                    <Badge color="gray">Not eligible this cycle</Badge>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {excl.reason}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                      {excl.excluded_by_name || 'Unknown'} · {String(excl.created_at || '').slice(0, 10)}
                    </div>
                    <Btn variant="ghost" size="sm" style={{ marginTop: 8, paddingLeft: 0 }}
                      onClick={() => restoreEligibility(u)}>Mark eligible</Btn>
                  </Card>
                );
              }
              return (
                <Card key={u.id} style={{ padding: '12px 14px', cursor: 'pointer' }} onClick={() => openReviewForm(u)}>
                  <UserLink id={u.id} name={u.name} initials={u.avatar_initials} subtitle={u.job_title}
                    avatarSize={32} nameSize={12} gap={9} style={{ marginBottom: 8 }}
                    onClick={e => e.stopPropagation()} />
                  {existingReview ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <RatingPill value={existingReview.calibration} />
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        Score: <strong>{existingReview.overall_score}</strong>/5
                      </span>
                    </div>
                  ) : (() => {
                    const status = selfEvalStatus(u.id);
                    if (status === 'pending_manager') {
                      return <div style={{ fontSize: 11, color: 'var(--blue-700)', fontWeight: 500 }}>Pending manager review</div>;
                    }
                    if (status === 'in_progress') {
                      return <div style={{ fontSize: 11, color: 'var(--amber-700)' }}>Self-eval in progress</div>;
                    }
                    return <div style={{ fontSize: 11, color: 'var(--amber-700)' }}>Review not started</div>;
                  })()}
                  <button
                    onClick={e => { e.stopPropagation(); setExclModal({ id: u.id, name: u.name }); setExclReason(''); }}
                    style={{ background: 'none', border: 'none', padding: '6px 0 0', cursor: 'pointer',
                             fontSize: 10.5, color: 'var(--text-3)', textDecoration: 'underline' }}>
                    Not eligible this cycle
                  </button>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Reviews table */}
      <SectionTitle>{isManager ? 'Submitted reviews' : 'Your reviews'}</SectionTitle>
      <Card style={{ padding: 0, overflow: 'hidden', marginTop: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)' }}>
              {['Employee','Type','Overall score','Calibration','Notes','Submitted', ...(isAdmin ? [''] : [])].map(h => (
                <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                <td style={{ padding: '11px 14px' }}>
                  <UserLink id={r.reviewee_id} name={r.reviewee_name} initials={r.reviewee_initials}
                    subtitle={`by ${r.reviewer_name}`} avatarSize={26} gap={8} />
                </td>
                <td style={{ padding: '11px 14px' }}><Badge color="blue">{r.review_type}</Badge></td>
                <td style={{ padding: '11px 14px' }}>
                  <span style={{ fontWeight: 500, fontSize: 16, color: scoreColor(r.overall_score) }}>
                    {r.overall_score}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>/5</span>
                </td>
                <td style={{ padding: '11px 14px' }}><RatingPill value={r.calibration} /></td>
                <td style={{ padding: '11px 14px', color: 'var(--text-2)', maxWidth: 360, minWidth: 220 }}>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: 12 }}>{r.summary_notes || '—'}</div>
                </td>
                <td style={{ padding: '11px 14px', color: 'var(--text-3)', fontSize: 11 }}>
                  {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}
                </td>
                {isAdmin && (
                  <td style={{ padding: '11px 14px' }}>
                    {(() => {
                      const rowCycle = cycles.find(c => String(c.id) === String(r.cycle_id));
                      const rowClosed = rowCycle?.status === 'closed' && Number(rowCycle?.program_id) === 1;
                      if (rowClosed) {
                        return <Btn variant="ghost" size="sm" onClick={() => navigate(`/reviews/edit/${r.reviewee_id}?cycle=${r.cycle_id}`)}>View</Btn>;
                      }
                      return (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Btn variant="secondary" size="sm" onClick={() => openReviewForm({ id: r.reviewee_id, name: r.reviewee_name })}>Edit</Btn>
                          <Btn variant="ghost" size="sm" onClick={() => handleDeleteReview(r.id, r.reviewee_name)} style={{ color: 'var(--red-500)' }}>Archive</Btn>
                        </div>
                      );
                    })()}
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} style={{ padding: '28px 14px', textAlign: 'center', color: 'var(--text-3)' }}>No reviews for this cycle yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Create cycle modal */}
      <Modal open={showCycle} onClose={() => setShowCycle(false)} title="New review cycle" width={440}>
        <form onSubmit={handleCreateCycle}>
          <Field label="Cycle name" required>
            <Input value={cycleForm.name} onChange={e => setCycleForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Cycle 1 · Jan–Apr 2027" />
          </Field>
          <Field label="Program" required>
            <Select value={cycleForm.program_id} onChange={e => setCycleForm(f => ({ ...f, program_id: e.target.value }))}>
              {programs.length === 0 && <option value="1">Standard PR Program</option>}
              {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Start date" required>
              <Input type="date" value={cycleForm.start_date} onChange={e => setCycleForm(f => ({ ...f, start_date: e.target.value }))} />
            </Field>
            <Field label="End date" required>
              <Input type="date" value={cycleForm.end_date} onChange={e => setCycleForm(f => ({ ...f, end_date: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Cycle #">
              <Select value={cycleForm.cycle_number} onChange={e => setCycleForm(f => ({ ...f, cycle_number: e.target.value }))}>
                <option value="">—</option>
                <option value="1">1 (Jan–Apr)</option>
                <option value="2">2 (Apr–Aug)</option>
                <option value="3">3 (Aug–Dec) 🏆</option>
              </Select>
            </Field>
            <Field label="Year">
              <Input type="number" value={cycleForm.year} onChange={e => setCycleForm(f => ({ ...f, year: e.target.value }))} placeholder="2026" />
            </Field>
            <Field label="Status">
              <Select value={cycleForm.status} onChange={e => setCycleForm(f => ({ ...f, status: e.target.value }))}>
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </Select>
            </Field>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <input type="checkbox" id="is_annual" checked={cycleForm.is_annual || false}
              onChange={e => setCycleForm(f => ({ ...f, is_annual: e.target.checked }))} />
            <label htmlFor="is_annual" style={{ fontSize: 13, cursor: 'pointer' }}>
              Annual cycle (Dec) — triggers bonus calculation
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => setShowCycle(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create cycle'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Not-eligible modal. Spells out all four consequences before confirming —
          this changes the person's annual bonus average, so it shouldn't be a
          one-click surprise. */}
      <Modal open={!!exclModal} onClose={() => setExclModal(null)}
        title={exclModal ? `Mark not eligible — ${exclModal.name}` : ''} width={460}>
        <form onSubmit={submitExclusion}>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 12 }}>
            Cycle: <strong>{cycles.find(c => String(c.id) === String(cycleId))?.name || '—'}</strong>
          </div>
          <Field label="Reason" required>
            <Textarea value={exclReason} onChange={e => setExclReason(e.target.value)} rows={3}
              placeholder="e.g. Joined 12 Aug, three weeks into the cycle." />
          </Field>
          <div style={{ background: 'var(--amber-50, #FDF6E3)', color: 'var(--amber-700)', borderRadius: 8,
                        padding: '10px 13px', fontSize: 11.5, lineHeight: 1.6, margin: '4px 0 14px' }}>
            For this cycle they will be excluded from:
            <div style={{ marginTop: 4 }}>
              · the performance review · KPI self-evaluation and scoring<br />
              · reminder emails · annual bonus averaging and champion eligibility
            </div>
            Existing reviews and scores are kept, and this can be undone.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => setExclModal(null)}>Cancel</Btn>
            <Btn type="submit" disabled={saving || !exclReason.trim()}>
              {saving ? 'Saving…' : 'Mark not eligible'}
            </Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
